import axios from 'axios';

import { useClaim } from '~/components/ClaimContainer';
import type {
  ClaimConductorHandlingResponse,
  ConductorHandlingSwitchRequest,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

/** The route of each switch: taking the claim over from Clive, or handing it back. */
export type HandlingSwitch = 'take-over' | 'hand-back';

/**
 * Switches who drives the claim, as the signed-in handler, then reads the claim's handling again.
 *
 * A 409 is the claim already being where the handler meant to put it — switched from another tab,
 * or by a colleague — so it is not an error: the read that follows shows the claim as it is. Any
 * other failure is reported here and rethrown, so the take-over dialog stays open with its reason
 * to retry.
 */
export const useSwitchHandling = (onHandlingUpdate: () => Promise<unknown>) => {
  const { claim } = useClaim({ claimRequired: true });

  return async (handlingSwitch: HandlingSwitch, reason?: string): Promise<void> => {
    try {
      await axios.post<ClaimConductorHandlingResponse>(
        `/api/v1/claims/${claim.id}/conductor/handling/${handlingSwitch}`,
        { reason } satisfies ConductorHandlingSwitchRequest
      );
    } catch (error) {
      // `isAxiosError` only narrows the type. A status under 400 never lands here (axios resolves it),
      // so 409 is the one rejection that is not a failure: the switch was already made.
      const isAlreadySwitched = axios.isAxiosError(error) && error.response?.status === 409;
      if (!isAlreadySwitched) {
        await reportAxiosError(error);
        throw error;
      }
    } finally {
      await onHandlingUpdate();
    }
  };
};
